import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { DishDto, DishInput, InviteCreatedDto, KitchenInput, MeDto, PlanDto, Role, StaffMemberDto } from '@tiffinify/contracts';
import { daysLabel, formatShort, mealsOf, sameDays, slotOf, type Food, type Meal, type Slot } from '@tiffinify/core';
import { PageLoader, Badge, Banner, BtnPrimary, BtnSecondary, Card, CardTitle, ToggleRow, Chip, Chips, useConfirm, DayPicker, DietChips, DietPicker, EmptyFilterState, ErrorText, Field, FoodMark, Helper, Icon, IconBtn, KV, MealPicker, Mono, SearchField, Sheet, Skel, Steps, TaskTitle, TiffinMini, TopBar, LoadError, type IconName } from '../components/ui';
import { ApiClientError, del, post, put } from '../lib/api';
import { COMMON_DISHES, type CommonDish } from '../lib/commonDishes';
import { errorMessage, formatInr, mealsLabel } from '../lib/format';
import { useEnterKitchen, useItems, useMe, useSettings, useStaff, useVersion } from '../lib/queries';
import { Card as PCard, Group, Ic, Row } from '../components/p';

const roleLabel = (r: Role) => (r === 'owner' ? 'Owner' : 'Staff');

/** "26 Oct 2026, 3:42 pm" in the phone's time zone. */
const dateTime = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

/** Dishes matching a search, a category ('All' for any) and a diet. */
function filterDishes<T extends { name: string; food: Food; category: string }>(dishes: readonly T[], q: string, category: string, diet: Food | 'all'): T[] {
  const s = q.trim().toLowerCase();
  return dishes.filter((d) => (category === 'All' || d.category === category) && (diet === 'all' || d.food === diet)
    && (!s || d.name.toLowerCase().includes(s) || d.category.toLowerCase().includes(s)));
}

/** "All categories", then each category with how many dishes it has. */
function CategoryChips({ dishes, value, onChange }: { dishes: ReadonlyArray<{ category: string }>; value: string; onChange: (c: string) => void }) {
  const categories = [...new Set(dishes.map((d) => d.category))].sort();
  // One category is nothing to choose between.
  if (categories.length < 2) return null;
  return (
    <Chips sm scroll label="Category">
      <Chip on={value === 'All'} count={dishes.length} onClick={() => onChange('All')}>All categories</Chip>
      {categories.map((c) => <Chip key={c} on={value === c} count={dishes.filter((d) => d.category === c).length} onClick={() => onChange(c)}>{c}</Chip>)}
    </Chips>
  );
}

/** When customers' pause requests for a day close, the evening before (kitchen time). */
const CUTOFFS = [
  { time: '18:00', label: '6 pm' },
  { time: '20:00', label: '8 pm' },
  { time: '21:00', label: '9 pm' },
  { time: '22:00', label: '10 pm' },
  { time: '23:59', label: 'Midnight' },
];

export function Settings() {
  const [params] = useSearchParams();
  const setup = params.get('setup') === '1';
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useSettings();
  const s = q.data;
  const staff = useMe().data?.user.role === 'staff';
  const page = pageOf(useParams().page);
  const home = !setup && !page;
  // Setup walks every rule on one page; otherwise each short page shows its own part.
  const on = (p: SettingsPage) => setup || page === p;
  const saves = setup || page === 'kitchen' || page === 'payments' || page === 'app';

  const [name, setName] = useState('');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [meals, setMeals] = useState<Slot>('lunch+dinner');
  const [remind, setRemind] = useState(3);
  const [upi, setUpi] = useState('');
  const [cutoff, setCutoff] = useState('23:59');
  const [printing, setPrinting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!s || loaded) return;
    const k = s.kitchen;
    setName(k.name); setDays(k.deliveryDays); setMeals(k.meals); setRemind(k.remindDaysBefore); setUpi(k.upiId ?? ''); setCutoff(s.pauseRequestCutoff ?? '23:59'); setPrinting(!!s.printing);
    setLoaded(true);
  }, [s, loaded]);

  const refresh = () => qc.invalidateQueries();

  const saveKitchen = useMutation({
    mutationFn: async () => {
      await put('/settings/kitchen', { name, upiId: upi.trim() || null, deliveryDays: days, remindDaysBefore: remind, meals } satisfies KitchenInput);
      if (s && cutoff !== s.pauseRequestCutoff) await put('/settings/pause-cutoff', { time: cutoff });
      if (s && printing !== s.printing) await put('/settings/printing', { on: printing });
    },
    onSuccess: async () => { await refresh(); setNote('Saved.'); setFields({}); if (setup) nav('/import?from=setup'); },
    onError: (e) => { setNote(''); if (e instanceof ApiClientError) setFields(e.fields); },
  });
  const logout = async () => { await post('/auth/logout'); qc.clear(); nav('/welcome', { replace: true }); };

  return (
    <main className="main task">
      {setup ? <div className="stack"><Mono>Step 2 of 3 · Your rules</Mono><Steps n={2} /></div> : <TopBar to={page ? '/settings' : '/more'} label={page ? 'Back to Settings' : 'Back to More'} />}
      <TaskTitle title={setup ? 'Your rules' : page ? PAGES[page].title : 'Settings'} sub={setup ? 'Pre-filled for a diet & fitness kitchen. Change anything, any time.' : page ? PAGES[page].sub : 'Short pages. Each line shows how it’s set now.'} />

      {q.isError && !s ? <LoadError what="settings" onRetry={() => void q.refetch()} /> : !s ? <PageLoader label="Opening settings" /> : (
        <>
          {staff && <Banner>Only the kitchen owner can change these. You can see them here.</Banner>}
          {home && <SettingsHome s={s} upi={upi} remind={remind} printing={printing} />}

          {on('kitchen') && <>
          <Card pad>
            <Field label="Kitchen name" id="k-name" value={name} onChange={setName} error={fields.name} />
          </Card>

          <Card pad>
            <CardTitle title="Meals you serve" />
            <MealPicker label="Meals you serve" value={meals} onChange={setMeals} />
            <Helper>Only these meals show on Today, the kitchen list and new plans. Existing customers keep their plans.</Helper>
            <CardTitle title="Delivery days" />
            <DayPicker label="Delivery days" value={days} onChange={setDays} />
            <Helper>Plans count these days unless they pick their own (say Mon, Tue, Wed or Sundays only), so a 26-day plan runs {days.length === 6 && !days.includes(7) ? 'Monday to Saturday' : 'on the days you pick'}. Changing them moves those plans’ end dates.</Helper>
            <Holidays holidays={s.holidays} onChange={refresh} />
          </Card>
          </>}

          {on('selling') && <>
          {!setup && <PCard><Row to="/items" left={<Ic icon="receipt" tone="s" />} t="Items for Menus" s="Sweets by weight, packs, plates and gift boxes" chev /></PCard>}
          <Plans plans={s.plans} onChange={refresh} served={mealsOf(meals)} kitchenDays={days} />
          <Dishes dishes={s.dishes} onChange={refresh} />
          </>}
          {on('delivery') && <Areas areas={s.areas} onChange={refresh} />}

          {on('payments') && <Card pad>
            <CardTitle title="Messages and payments" />
            <div className="stack">
              <span className="label">Renewal reminder starts</span>
              <Chips label="Renewal reminder">{[1, 2, 3, 5, 7].map((n) => <Chip key={n} on={remind === n} onClick={() => setRemind(n)}>{n} {n === 1 ? 'day' : 'days'} before</Chip>)}</Chips>
            </div>
            <Field label="UPI ID for payment links" id="k-upi" value={upi} onChange={setUpi} placeholder="yourkitchen@upi" hint="Added to payment and renewal messages." error={fields.upiId} />
            <div className="stack">
              <span className="label">Pause requests close the day before at</span>
              <Chips label="Pause requests close">{CUTOFFS.map((c) => <Chip key={c.time} on={cutoff === c.time} onClick={() => setCutoff(c.time)}>{c.label}</Chip>)}</Chips>
              <Helper>For customers asking from their My plan page. Today can never be asked for; you can still pause any day yourself.</Helper>
            </div>
          </Card>}

          {on('app') && <Card className="toggle-card">
            <ToggleRow title="Print labels and delivery lists" sub="Turn on when you have a printer. Uses standard A4 sticker sheets (24 labels, 70 × 37 mm) and plain A4." on={printing} onChange={setPrinting} />
          </Card>}

          {saves && <>
          <ErrorText>{saveKitchen.error ? errorMessage(saveKitchen.error) : ''}</ErrorText>
          {note && <p className="st-ok" role="status">{note}</p>}
          <BtnPrimary busy={saveKitchen.isPending} label={saveKitchen.isPending ? 'Saving…' : setup ? 'Next: add customers' : 'Save'} icon={setup ? 'arrow' : 'check'} onClick={() => saveKitchen.mutate()} disabled={saveKitchen.isPending} />
          </>}
          {!setup && page === 'team' && <><Staff /><Kitchens /></>}
          {!setup && page === 'app' && <><AdminLink /><About /><BtnSecondary label="Sign out" onClick={logout} /></>}
        </>
      )}
    </main>
  );
}

type SettingsPage = 'kitchen' | 'selling' | 'delivery' | 'payments' | 'team' | 'app';

const PAGES: Record<SettingsPage, { title: string; sub: string; icon: IconName }> = {
  kitchen: { title: 'Kitchen and days', sub: 'Name, meals you serve, days you cook and holidays.', icon: 'calendar' },
  selling: { title: 'Plans, Menus and items', sub: 'What customers can buy from you.', icon: 'pot' },
  delivery: { title: 'Delivery', sub: 'The areas you deliver to.', icon: 'bike' },
  payments: { title: 'Payments and messages', sub: 'UPI, reminders and pause requests.', icon: 'rupee' },
  team: { title: 'Team', sub: 'Owners and staff, and your other kitchens.', icon: 'people' },
  app: { title: 'App and printing', sub: 'Printing, version and signing out.', icon: 'print' },
};
const pageOf = (p: string | undefined): SettingsPage | null => (p && p in PAGES ? (p as SettingsPage) : null);

/** The Settings home: two groups of three short pages, each line saying how it is set now. */
function SettingsHome({ s, upi, remind, printing }: { s: NonNullable<ReturnType<typeof useSettings>['data']>; upi: string; remind: number; printing: boolean }) {
  const team = useStaff().data?.members.length;
  const items = useItems().data?.items.length ?? 0;
  const summary: Record<SettingsPage, string> = {
    kitchen: `${mealsOf(s.kitchen.meals).length} ${mealsOf(s.kitchen.meals).length === 1 ? 'meal' : 'meals'} · ${daysLabel(s.kitchen.deliveryDays)} · ${s.holidays.length} ${s.holidays.length === 1 ? 'holiday' : 'holidays'}`,
    selling: `${s.plans.length} ${s.plans.length === 1 ? 'plan' : 'plans'} · ${s.dishes.length} ${s.dishes.length === 1 ? 'dish' : 'dishes'}${items ? ` · ${items} ${items === 1 ? 'item' : 'items'}` : ''}`,
    delivery: `${s.areas.length} ${s.areas.length === 1 ? 'area' : 'areas'}`,
    payments: `UPI ${upi ? 'set' : 'not set'} · reminders ${remind} ${remind === 1 ? 'day' : 'days'} before`,
    team: team === undefined ? 'Owners and staff' : `${team} ${team === 1 ? 'person' : 'people'}`,
    app: `Printing ${printing ? 'on' : 'off'} · version ${__APP_VERSION__}`,
  };
  const group = (title: string, pages: SettingsPage[]) => (
    <Group label={title}>
      <PCard>
        {pages.map((p) => <Row key={p} to={`/settings/${p}`} left={<Ic icon={PAGES[p].icon} tone={p === 'payments' ? 'g' : p === 'selling' ? 's' : ''} />} t={PAGES[p].title} s={summary[p]} chev />)}
      </PCard>
    </Group>
  );
  return <>{group('Your kitchen', ['kitchen', 'selling', 'delivery'])}{group('People and money', ['payments', 'team', 'app'])}</>;
}

function Staff() {
  const { confirm, sheet: confirmSheet } = useConfirm();
  const qc = useQueryClient();
  const q = useStaff();
  const st = q.data;
  const [created, setCreated] = useState<InviteCreatedDto | null>(null);
  const [msg, setMsg] = useState('');
  const [copyErr, setCopyErr] = useState('');
  const changed = () => qc.invalidateQueries({ queryKey: ['staff'] });

  const invite = useMutation({
    mutationFn: () => post<InviteCreatedDto>('/staff/invites'),
    onSuccess: (r) => { setCreated(r); setMsg(''); setCopyErr(''); void changed(); },
  });
  const withdraw = useMutation({
    mutationFn: (id: string) => del(`/staff/invites/${id}`),
    onSuccess: (_, id) => { if (created?.id === id) setCreated(null); setMsg('Invite withdrawn.'); void changed(); },
  });
  const remove = useMutation({
    mutationFn: (m: StaffMemberDto) => del(`/staff/members/${m.userId}`),
    onSuccess: (_, m) => { setMsg(`${m.name} removed.`); void changed(); },
  });
  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) => put(`/staff/members/${userId}/role`, { role }),
    onSuccess: (_, v) => {
      const target = st?.members.find((m) => m.userId === v.userId);
      setMsg(`${target?.name ?? 'They'} ${v.role === 'owner' ? 'is now an owner' : 'is now staff'}.`);
      void changed();
    },
  });

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopyErr(''); setMsg('Link copied.');
    } catch {
      setMsg(''); setCopyErr('Could not copy. Press and hold the link to copy it.');
    }
  };
  const owners = st?.members.filter((x) => x.role === 'owner').length ?? 0;
  const onRoleSelect = (m: StaffMemberDto, role: Role) => {
    if (role === m.role) return;
    void confirm(role === 'owner'
      ? { title: `Make ${m.name} an owner?`, body: 'Owners see revenue, change the kitchen’s rules and manage staff.', yes: 'Make owner' }
      : { title: `Make ${m.name} staff?`, body: 'They will no longer see revenue or manage staff.', yes: 'Make staff' })
      .then((ok) => { if (ok) changeRole.mutate({ userId: m.userId, role }); });
  };
  const confirmRemove = (m: StaffMemberDto) => {
    void confirm({ title: `Remove ${m.name}?`, body: 'They are signed out of this kitchen straight away.', yes: 'Remove', danger: true })
      .then((ok) => { if (ok) remove.mutate(m); });
  };
  const busyErr = invite.error ?? withdraw.error ?? remove.error ?? changeRole.error ?? q.error;

  return (
    <Card pad>
      {confirmSheet}
      <CardTitle title="Staff" right={st ? <Mono>{st.members.length} {st.members.length === 1 ? 'person' : 'people'}</Mono> : <Skel w={64} />} />
      {st?.canManage && <Helper>Owners see revenue, change the kitchen’s rules and manage the team. Staff cook, pack and deliver.</Helper>}
      <div>
        {!st ? [0, 1].map((i) => (
          <div className="list-row" key={i}><span className="grow"><Skel w={120} /><Skel w={180} /></span></div>
        )) : st.members.map((m) => (
          <div className="list-row" key={m.userId}>
            <span className="grow">
              <b className="ellipsis">{m.name}{m.you && ' (you)'}</b>
              <small className="ellipsis">{m.email || roleLabel(m.role)}</small>
            </span>
            {st.canManage && !m.you ? (
              <span className="row member-actions">
                <span className="role-select-wrap">
                  {/* The value comes from the server, so a cancelled change shows the role unchanged. */}
                  <select className={`role-select${m.role === 'owner' ? ' ok' : ''}`} value={m.role} disabled={changeRole.isPending} onChange={(e) => onRoleSelect(m, e.target.value as Role)} aria-label={`Role for ${m.name}`}>
                    <option value="owner">Owner</option>
                    <option value="staff" disabled={m.role === 'owner' && owners <= 1}>Staff</option>
                  </select>
                  <Icon name="down" size={11} sw={2.6} />
                </span>
                {(m.role !== 'owner' || owners > 1) && <IconBtn icon="trash" label={`Remove ${m.name}`} ghost onClick={() => confirmRemove(m)} />}
              </span>
            ) : (
              <Badge kind={m.role === 'owner' ? 'ok' : undefined}>{roleLabel(m.role)}</Badge>
            )}
          </div>
        ))}
      </div>

      {st?.canManage && (
        <>
          {st.invites.length > 0 && (
            <div>
              <div className="setting sub-head"><span className="grow"><b>Open invites</b><small>Each link works once.</small></span></div>
              {st.invites.map((i) => (
                <div className="list-row" key={i.id}>
                  <span className="grow"><b>Invite link</b><small>Expires {dateTime(i.expiresAt)}</small></span>
                  <IconBtn icon="close" label="Withdraw invite" ghost onClick={() => withdraw.mutate(i.id)} />
                </div>
              ))}
            </div>
          )}
          {created && (
            <div className="stack inline-form">
              <Field label="Invite link" id="st-link" value={created.link} readOnly hint={`Send it to your staff member. It works once, until ${dateTime(created.expiresAt)}.`} />
              <BtnSecondary label="Send on WhatsApp" icon="chat" sm href={created.share.link} />
              <BtnSecondary label="Copy link" icon="sheet" sm onClick={() => void copy(created.link)} />
            </div>
          )}
          <BtnSecondary busy={invite.isPending} label="Invite staff" icon="plus" sm onClick={() => invite.mutate()} />
        </>
      )}
      <ErrorText>{copyErr || (busyErr ? errorMessage(busyErr) : '')}</ErrorText>
      {msg && <p className="st-ok" role="status">{msg}</p>}
    </Card>
  );
}

/** Only for people who work in more than one kitchen. Switching starts the app afresh in the other kitchen. */
function Kitchens() {
  const enter = useEnterKitchen();
  const me = useMe().data;
  const sw = useMutation({
    mutationFn: (kitchenId: string) => post<MeDto>('/auth/switch', { kitchenId }),
    onSuccess: enter,
  });
  if (!me || me.kitchens.length < 2) return null;
  return (
    <Card pad>
      <CardTitle title="Your kitchens" />
      <div>
        {me.kitchens.map((k) => {
          const current = k.id === me.kitchen.id;
          return (
            <button key={k.id} type="button" className="setting" aria-current={current || undefined} aria-label={current ? `${k.name}, current kitchen` : `Switch to ${k.name}`} onClick={() => !current && !sw.isPending && sw.mutate(k.id)}>
              <span className="grow"><b>{k.name}</b><small>{roleLabel(k.role)}</small></span>
              {current ? <Badge kind="ok" icon="check">Current</Badge> : sw.isPending && sw.variables === k.id ? <TiffinMini /> : <Icon name="right" size={18} sw={2.2} />}
            </button>
          );
        })}
      </div>
      <ErrorText>{sw.error ? errorMessage(sw.error) : ''}</ErrorText>
    </Card>
  );
}

/** Only for the Tiffinify team: every kitchen and person on Tiffinify. */
function AdminLink() {
  const me = useMe().data;
  if (!me?.user.isTiffinifyTeam) return null;
  return (
    <Card pad>
      <CardTitle title="Tiffinify team" />
      <Helper>Every kitchen and person on Tiffinify. Only you and the rest of the team see this.</Helper>
      <BtnSecondary sm label="Open the team pages" icon="arrow" to="/admin" />
    </Card>
  );
}

const ENV_LABEL: Record<string, string> = { production: 'Production', staging: 'Staging', development: 'Development' };

/** "abc1234-dirty" and "abc1234" are the same code; "dev" means the build stamp is unknown. */
const sameBuild = (a: string, b: string) => a === 'dev' || b === 'dev' || a.replace(/-dirty$/, '') === b.replace(/-dirty$/, '');
const sameVersion = (a?: string, b?: string) => !a || !b || a === 'dev' || b === 'dev' || a === b;

/** The version people see. The git commits stay out of sight: `GET /api/version` has them for support. */
function About() {
  const v = useVersion();
  const server = v.data;
  const stale = !!server && (!sameBuild(__APP_COMMIT__, server.commit) || !sameVersion(__APP_VERSION__, server.version));
  return (
    <Card pad>
      <CardTitle title="About" />
      {stale && <Banner action={<button type="button" className="link" onClick={() => window.location.reload()}>Reload</button>}>A newer version is available</Banner>}
      <div className="about">
        <KV k="Version" v={__APP_VERSION__} />
        <KV k="Updated" v={dateTime(__APP_BUILT_AT__)} />
        {server && server.env !== 'production' && <KV k="Environment" v={ENV_LABEL[server.env] ?? server.env} />}
      </div>
    </Card>
  );
}

function Holidays({ holidays, onChange }: { holidays: Array<{ id: string; day: string; label: string }>; onChange: () => void }) {
  const [day, setDay] = useState('');
  const [label, setLabel] = useState('');
  const [msg, setMsg] = useState('');
  const add = useMutation({
    mutationFn: () => post<{ plansMoved: number }>('/settings/holidays', { day, label }),
    onSuccess: (r) => { setDay(''); setLabel(''); setMsg(r.plansMoved ? `${r.plansMoved} plans moved by a day.` : 'Added. No running plan was affected.'); onChange(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => del<{ plansMoved: number }>(`/settings/holidays/${id}`),
    onSuccess: (r) => { setMsg(r.plansMoved ? `${r.plansMoved} plans moved back.` : 'Removed.'); onChange(); },
  });
  return (
    <div>
      <div className="setting sub-head"><span className="grow"><b>Holidays</b><small>No meals on these dates. Every running plan moves by a day.</small></span></div>
      {holidays.map((h) => (
        <div className="list-row" key={h.id}>
          <span className="grow"><b>{formatShort(h.day)}</b>{h.label && <small>{h.label}</small>}</span>
          <IconBtn icon="trash" label={`Remove ${h.label || formatShort(h.day)}`} ghost onClick={() => remove.mutate(h.id)} />
        </div>
      ))}
      <div className="stack inline-form">
        <Field label="Date" id="h-day" type="date" value={day} onChange={setDay} />
        <Field label="Name (optional)" id="h-label" value={label} onChange={setLabel} placeholder="Diwali" />
        <BtnSecondary busy={add.isPending} label="Add holiday" icon="plus" sm onClick={() => add.mutate()} disabled={!day || add.isPending} />
        <ErrorText>{add.error ? errorMessage(add.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
        {msg && <p className="st-ok" role="status">{msg}</p>}
      </div>
    </div>
  );
}

function Plans({ plans, onChange, served, kitchenDays }: { plans: PlanDto[]; onChange: () => void; served: Meal[]; kitchenDays: number[] }) {
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const done = () => { setEditing(null); onChange(); };
  return (
    <Card pad>
      <CardTitle title="Plans" right={<Mono>Custom prices too</Mono>} />
      <div>
        {plans.map((p) => editing === p.id ? (
          <PlanForm key={p.id} initial={p} served={served} kitchenDays={kitchenDays} onDone={done} />
        ) : (
          <div className="list-row" key={p.id}>
            <span className="grow"><b>{p.name}</b><small>{[`${p.mealDays} meal days`, mealsLabel(p.slot).toLowerCase(), p.days ? daysLabel(p.days) : null].filter(Boolean).join(' · ')}</small></span>
            <span className="price">{formatInr(p.price)}</span>
            <IconBtn icon="edit" label={`Edit ${p.name}`} ghost onClick={() => setEditing(p.id)} />
          </div>
        ))}
      </div>
      {editing === 'new' ? <PlanForm served={served} kitchenDays={kitchenDays} onDone={done} /> : <BtnSecondary label="Add a plan" icon="plus" sm onClick={() => setEditing('new')} />}
    </Card>
  );
}

function PlanForm({ initial, onDone, served, kitchenDays }: { initial?: PlanDto; onDone: () => void; served: Meal[]; kitchenDays: number[] }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [mealDays, setMealDays] = useState(String(initial?.mealDays ?? 26));
  const [slot, setSlot] = useState<Slot>(initial?.slot ?? slotOf([served.includes('lunch') ? 'lunch' : served[0] ?? 'lunch']));
  const [price, setPrice] = useState(String(initial?.price ?? ''));
  // Any combination of weekdays; the kitchen's days are saved as "follow the kitchen".
  const [days, setDays] = useState<number[]>(initial?.days ?? kitchenDays);
  const body = { name, mealDays: Number(mealDays), slot, price: Number(price), days: sameDays(days, kitchenDays) ? null : days };
  const save = useMutation({ mutationFn: () => (initial ? put(`/settings/plans/${initial.id}`, body) : post('/settings/plans', body)), onSuccess: onDone });
  const remove = useMutation({ mutationFn: () => del(`/settings/plans/${initial!.id}`), onSuccess: onDone });
  return (
    <div className="stack inline-form">
      <Field label="Plan name" id="pl-name" value={name} onChange={setName} placeholder="Lunch · 26 days" />
      <MealPicker label="Meals each day" value={slot} onChange={setSlot} allowed={served} />
      <div className="stack">
        <span className="label">Delivery days</span>
        <DayPicker label="Delivery days" value={days} onChange={setDays} />
      </div>
      <div className="two">
        <Field label="Meal days" id="pl-days" value={mealDays} onChange={(v) => setMealDays(v.replace(/\D/g, ''))} inputMode="numeric" />
        <Field label="Price" id="pl-price" value={price} onChange={(v) => setPrice(v.replace(/\D/g, ''))} prefix="₹" inputMode="numeric" />
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
      <div className="card-actions-responsive">
        <BtnSecondary busy={save.isPending} label="Save plan" icon="check" sm onClick={() => save.mutate()} disabled={save.isPending || name.trim().length < 2 || !Number(mealDays) || price === ''} />
        {initial ? <BtnSecondary busy={remove.isPending} label="Delete" icon="trash" sm danger onClick={() => remove.mutate()} disabled={remove.isPending} /> : <BtnSecondary label="Cancel" sm onClick={onDone} />}
      </div>
    </div>
  );
}

function Areas({ areas, onChange }: { areas: Array<{ id: string; name: string }>; onChange: () => void }) {
  const [name, setName] = useState('');
  const add = useMutation({ mutationFn: () => post('/settings/areas', { name }), onSuccess: () => { setName(''); onChange(); } });
  const remove = useMutation({ mutationFn: (id: string) => del(`/settings/areas/${id}`), onSuccess: onChange });
  return (
    <Card pad>
      <CardTitle title="Delivery areas" right={<Mono>Used on the kitchen list</Mono>} />
      <Chips label="Delivery areas">
        {areas.map((a) => (
          <button key={a.id} type="button" className="chip" aria-label={`Remove ${a.name}`} onClick={() => remove.mutate(a.id)}><span className="label-text">{a.name}</span><Icon name="close" size={14} sw={2.4} /></button>
        ))}
      </Chips>
      <div className="row add-row">
        <div className="grow"><Field label="New area" id="a-name" value={name} onChange={setName} placeholder="e.g. Jayanagar" /></div>
        <IconBtn icon="plus" label="Add area" onClick={() => name.trim() && add.mutate()} />
      </div>
      <ErrorText>{add.error ? errorMessage(add.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
    </Card>
  );
}

/** The kitchen's own dishes, which customers can choose for their meals. */
function Dishes({ dishes, onChange }: { dishes: DishDto[]; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [library, setLibrary] = useState(false);
  const [category, setCategory] = useState('All');
  const [diet, setDiet] = useState<Food | 'all'>('all');
  const [search, setSearch] = useState('');
  const shown = filterDishes(dishes, search, category, diet);
  const remove = useMutation({ mutationFn: (id: string) => del(`/settings/dishes/${id}`), onSuccess: onChange });

  return (
    <Card pad>
      <CardTitle title="Dishes" right={<Mono>{dishes.length} {dishes.length === 1 ? 'dish' : 'dishes'}</Mono>} />
      <Helper>Dishes customers can choose for their meals, instead of the day’s special.</Helper>

      {dishes.length === 0 && !editing && (
        <div className="quick-start">
          <b>Start from the library</b>
          <p>{COMMON_DISHES.length} common dishes to add in a tap, or add your own.</p>
        </div>
      )}

      {dishes.length > 0 && (
        <div className="stack">
          {dishes.length > 6 && <SearchField value={search} onChange={setSearch} label="Search your dishes" placeholder="Dish or category" />}
          <CategoryChips dishes={dishes} value={category} onChange={setCategory} />
          <DietChips items={dishes} value={diet} onChange={setDiet} />
          <div className="dishes-menu-scroll">
            {shown.length === 0 ? (
              <EmptyFilterState
                title="No dishes match"
                desc={search ? `Nothing called “${search.trim()}”.` : 'Try another category or diet.'}
                onReset={() => { setSearch(''); setCategory('All'); setDiet('all'); }}
              />
            ) : shown.map((d) => (
              <div className="kitchen-dish-row" key={d.id}>
                <FoodMark food={d.food} />
                <span className="dish-card-name">{d.name}</span>
                <Badge>{d.category}</Badge>
                <IconBtn icon="trash" label={`Remove ${d.name}`} ghost disabled={remove.isPending && remove.variables === d.id} onClick={() => remove.mutate(d.id)} />
              </div>
            ))}
          </div>
          <ErrorText>{remove.error ? errorMessage(remove.error) : ''}</ErrorText>
        </div>
      )}

      {editing ? (
        <DishForm onDone={(saved) => { setEditing(false); if (saved) onChange(); }} />
      ) : (
        <div className="card-actions-responsive">
          <BtnSecondary sm ink={dishes.length === 0} label="Dish library" icon="sheet" onClick={() => setLibrary(true)} />
          <BtnSecondary sm label="Add your own" icon="plus" onClick={() => setEditing(true)} />
        </div>
      )}

      {library && <DishLibrary kitchenDishes={dishes} onChange={onChange} onClose={() => setLibrary(false)} />}
    </Card>
  );
}

/** The common dishes: tap one to add it to the kitchen's list (or take it off), or add a whole category at once. */
function DishLibrary({ kitchenDishes, onChange, onClose }: { kitchenDishes: DishDto[]; onChange: () => void; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [diet, setDiet] = useState<Food | 'all'>('all');
  const mine = new Map(kitchenDishes.map((d) => [d.name.toLowerCase(), d]));
  const shown = filterDishes(COMMON_DISHES, search, category, diet);
  const missing = shown.filter((d) => !mine.has(d.name.toLowerCase()));
  const onMenu = COMMON_DISHES.filter((d) => mine.has(d.name.toLowerCase())).length;
  const asInput = (d: CommonDish): DishInput => ({ name: d.name, food: d.food, category: d.category });

  const toggle = useMutation({
    mutationFn: (d: CommonDish) => {
      const existing = mine.get(d.name.toLowerCase());
      return existing ? del(`/settings/dishes/${existing.id}`) : post('/settings/dishes', asInput(d));
    },
    onSuccess: onChange,
  });
  const addAll = useMutation({
    mutationFn: () => post('/settings/dishes/batch', { dishes: missing.map(asInput) }),
    onSuccess: onChange,
  });
  const busy = toggle.isPending || addAll.isPending;

  return (
    <Sheet kicker="Dish library" title={`${onMenu} of ${COMMON_DISHES.length} on your list`} onClose={onClose} className="tall">
      <div className="sheet-toolbar">
        <SearchField value={search} onChange={setSearch} label="Search the library" placeholder="Chicken, paneer, bowl…" />
        <CategoryChips dishes={COMMON_DISHES} value={category} onChange={setCategory} />
        <DietChips items={COMMON_DISHES} value={diet} onChange={setDiet} />
      </div>
      <div className="sheet-scroll">
        {shown.length === 0 ? (
          <EmptyFilterState
            title="No dishes match"
            desc={search ? `Nothing called “${search.trim()}”.` : 'Try another category or diet.'}
            onReset={() => { setSearch(''); setCategory('All'); setDiet('all'); }}
          />
        ) : shown.map((d) => {
          const on = mine.has(d.name.toLowerCase());
          return (
            <button key={d.name} type="button" className={`dish-card selectable${on ? ' selected' : ''}`} aria-pressed={on} disabled={busy} onClick={() => toggle.mutate(d)}>
              <FoodMark food={d.food} />
              <span className="dish-card-name">{d.name}</span>
              <Badge>{d.category}</Badge>
              <span className="dish-card-pick" aria-hidden="true">
                {toggle.isPending && toggle.variables?.name === d.name ? <TiffinMini /> : <Icon name={on ? 'check' : 'plus'} size={14} sw={2.8} />}
              </span>
            </button>
          );
        })}
      </div>
      <ErrorText>{toggle.error ? errorMessage(toggle.error) : addAll.error ? errorMessage(addAll.error) : ''}</ErrorText>
      <div className="sheet-footer">
        {missing.length > 0
          ? <BtnSecondary sm label={`Add ${missing.length === shown.length ? 'all' : 'the other'} ${missing.length}`} icon="plus" busy={addAll.isPending} disabled={busy} onClick={() => addAll.mutate()} />
          : <small>All of these are on your list.</small>}
        <BtnPrimary label="Done" icon="check" onClick={onClose} />
      </div>
    </Sheet>
  );
}

const CATEGORIES = ['Meals', 'Combos', 'Bowls', 'Breakfast', 'High Protein', 'Snacks', 'Beverages'];

function DishForm({ onDone }: { onDone: (saved: boolean) => void }) {
  const [name, setName] = useState('');
  const [food, setFood] = useState<Food>('veg');
  const [category, setCategory] = useState('Meals');
  const save = useMutation({ mutationFn: () => post('/settings/dishes', { name, food, category } satisfies DishInput), onSuccess: () => onDone(true) });
  return (
    <div className="stack inline-form">
      <Field label="Dish name" id="d-name" value={name} onChange={setName} placeholder="e.g. Chicken dal rice" />
      <div className="stack"><span className="label">Diet</span><DietPicker label="Diet" value={food} onChange={setFood} /></div>
      <div className="stack">
        <span className="label">Category</span>
        <Chips label="Category">{CATEGORIES.map((c) => <Chip key={c} on={category === c} onClick={() => setCategory(c)}>{c}</Chip>)}</Chips>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <div className="card-actions-responsive">
        <BtnSecondary busy={save.isPending} label="Save dish" icon="check" sm onClick={() => save.mutate()} disabled={save.isPending || !name.trim()} />
        <BtnSecondary label="Cancel" sm onClick={() => onDone(false)} />
      </div>
    </div>
  );
}
